import React, { useState, useEffect } from 'react';
import { parseString } from 'xml2js';
import CourseMenu from './CourseMenu';
import CourseViewer from './CourseViewer';
import APIwrapper from '../lib/APIwrapper.util.js'
import scormApi12 from '../lib/scormApi12.util.js'
import scormApi2004 from '../lib/scormApi2004.util.js'
import scormToXAPIFunctions from '../lib/SCORMToXAPIFunctions.js'
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { BASE_URL, LRS_ENDPOINT, LRS_API_KEY, LRS_API_KEY_SECRETE, GetCoursesBaseUrl, SCORM_VIEWER_URL } from "../constants/config";
import Overlay from './Overlay';

let delayXApiRequest = null

const resetScorm = () => {
    APIwrapper.destroyWrapper()
    delete window.initializeCommunication
    delete window.terminateCommunication
    delete window.storeDataValue
    delete window.retrieveDataValue
    delete window.API
    delete window.API_1484_11
    delete window.xapi
    delete window.localStorage.APIScormConfig
    delete window.localStorage.learnerId
}

const getXapiConfig = (courseId, courseName, isScorm2004, registrationId) => {
    const lmsHomePage = SCORM_VIEWER_URL;
    const activityId = `${lmsHomePage}/${courseId}`;
    return {
        "lrs": {
            "endpoint": LRS_ENDPOINT,
            "user": LRS_API_KEY,
            "password": LRS_API_KEY_SECRETE
        },
        "isScorm2004": isScorm2004,
        "lmsHomePage": lmsHomePage,
        "courseId": activityId,
        "activityId": activityId,
        "registration": registrationId,
        "groupingContextActivity": {
            "definition": {
                "name": {
                    "en-US": courseName
                }
            },
            "id": activityId,
            "objectType": "Activity"
        }
    }
}

const initScorm = (courseId, courseName, userName, isScorm2004, registrationId) => {
    resetScorm()

    if (isScorm2004) {
        window.API_1484_11 = new scormApi2004.ScormAPI2004()
    } else {
        window.API = new scormApi12.ScormAPI()
    }

    const simplifyScorm = APIwrapper.findAPI(window)
    if (!simplifyScorm) {
        console.error('simplifyScorm not loaded', APIwrapper)
        return
    }
    const xApiFunctions = scormToXAPIFunctions.xapi()
    window.xapi = xApiFunctions
    const xApiConfig = getXapiConfig(courseId, courseName, isScorm2004, registrationId)
    window.localStorage.APIScormConfig = JSON.stringify(xApiConfig)

    if (isScorm2004) {
        simplifyScorm.cmi.learner_id = userName
    } else {
        simplifyScorm.cmi.core.student_id = userName
    }
    window.localStorage.learnerId = userName

    window.terminateCommunication = APIwrapper.terminateCommunication
    window.storeDataValue = APIwrapper.storeDataValue
    window.retrieveDataValue = APIwrapper.retrieveDataValue
    const firstState = xApiFunctions.myGetState()
    if (firstState) {
        if (isScorm2004) {
            simplifyScorm.cmi.entry = 'resume'
            simplifyScorm.cmi.location = firstState.location
            simplifyScorm.cmi.mode = firstState.mode
            simplifyScorm.cmi.learner_preference.audio_captioning = firstState.preferences?.audio_captioning
            simplifyScorm.cmi.learner_preference.audio_level = firstState.preferences?.audio_level
            simplifyScorm.cmi.learner_preference.delivery_speed = firstState.preferences?.delivery_speed
            simplifyScorm.cmi.learner_preference.language = firstState.preferences?.language
        } else {
            simplifyScorm.cmi.core.entry = 'resume'
            simplifyScorm.cmi.core.lesson_location = firstState.location
            simplifyScorm.cmi.core.lesson_mode = firstState.mode
            simplifyScorm.cmi.student_preference.text = firstState.preferences?.audio_captioning
            simplifyScorm.cmi.student_preference.audio = firstState.preferences?.audio_level
            simplifyScorm.cmi.student_preference.speed = firstState.preferences?.delivery_speed
            simplifyScorm.cmi.student_preference.language = firstState.preferences?.language
        }
        simplifyScorm.cmi.suspend_data = firstState.suspend_data
    }
    simplifyScorm.on(APIwrapper.getNameInit(isScorm2004), () => {
        xApiFunctions.initializeAttempt()
        if (isScorm2004) {
            simplifyScorm.cmi.completion_status = 'incomplete'
        } else {
            simplifyScorm.cmi.core.lesson_status = 'incomplete'
        }
    })
    simplifyScorm.on(APIwrapper.getNameCommit(isScorm2004), function () {
        xApiFunctions.setAttemptState()
    })
    simplifyScorm.on(APIwrapper.getNameSet(isScorm2004), (name, value) => {
        // we use a debounce to avoid too many requests to the LRS, but it is not mandatory
        clearTimeout(delayXApiRequest)
        delayXApiRequest = setTimeout(() => {
            xApiFunctions.setAttemptState()
        }, 1000)
        xApiFunctions.saveDataValue(name, value)
        if ((name === 'cmi.completion_status' || name === 'cmi.core.lesson_status') && value === 'completed') {
            // here you could manage when an SCORM course is completed
        }
    })
    simplifyScorm.on(APIwrapper.getNameFinish(isScorm2004), function () {
        xApiFunctions.terminateAttempt()
    })
}

function processItems(items) {
    if (!Array.isArray(items)) {
        return getContent(items);
    }

    return items.map(item => {
        const content = getContent(item);

        if (Array.isArray(item.item)) {
            content.items = processItems(item.item);
        }

        return content;
    });
}

function getContent(item) {
    return {
        title: item.title,
        href: item.href,
        identifierref: item.identifierref,
        identifier: item.identifier,
        parameters: item.parameters
    };
}

function setHref(item, resources) {
    if (item.identifierref) {
        let resource = null;
        if (Array.isArray(resources)) {
            resource = resources.find(r => r.identifier === item.identifierref);
        } else {
            resource = resources;
        }
        if (resource) {
            item.href = resource.href;
            item.type = resource.type;
            item.file = resource.file;
            item.scormtype = resource['adlcp:scormtype'];
        }
    }
    if (item.items) {
        item.items.forEach(subItem => setHref(subItem, resources));
    }
}

function extractTcConfig(content, key) {
    const regexMap = {
        TC_COURSE_ID: /TC_COURSE_ID\s*=\s*"([^"]*)"/,
        TC_COURSE_NAME: /TC_COURSE_NAME\s*=\s*({\s*"\s*en-US\s*"\s*:\s*"\s*[^"]*\s*"\s*}\s*);/,
        TC_COURSE_DESC: /TC_COURSE_DESC\s*=\s*({\s*"\s*en-US\s*"\s*:\s*"\s*[^"]*\s*"\s*}\s*);/
    };

    const regex = regexMap[key];
    if (!regex) return null;

    const match = content.match(regex);
    return match && match[1] ? match[1] : null;
}

function encodeUrlParams(params) {
    const encodedParams = [];
    for (const key in params) {
        if (params.hasOwnProperty(key)) {
            const encodedKey = encodeURIComponent(key);
            const encodedValue = encodeURIComponent(params[key]);
            encodedParams.push(encodedKey + "=" + encodedValue);
        }
    }
    return encodedParams.join('&');
}

const fetchUserCourse = async (userId, courseId) => {
    try {
        const response = await axios.get(`${BASE_URL}/api/user-course?userId=${userId}&courseId=${courseId}`);
        return response.data;
    } catch (error) {
        console.error('Failed to fetch courses:', error);
    }
};

function Course() {
    const { id } = useParams();

    const userId = 1; // We need to take this from the logged in user
    const courseId = id;

    const [dataLoaded, setDataLoaded] = useState(false);
    const [userData, setUserData] = useState(null);
    const [courseData, setCourseData] = useState(null);
    const [userCourseData, setUserCourseData] = useState(null);
    const [courseMenuData, setCourseMenuData] = useState(null);
    const [coursePageLink, setCoursePageLink] = useState(null);
    const [hasChapters, setHasChapters] = useState(true);

    const onPageLinkClick = (pageLink) => {
        setCoursePageLink(pageLink);
    };

    const coursesBaseUrl = GetCoursesBaseUrl();

    useEffect(() => {
        async function fetchUserCourseData() {
            const data = await fetchUserCourse(userId, courseId);
            setUserData(data.user);
            setCourseData(data.course);
            setUserCourseData(data.userCourse);
        }
        fetchUserCourseData();
    }, [courseId]);

    useEffect(() => {
        if (!courseData || !userData || !userCourseData) {
            return;
        }

        courseData.path = `${courseData.id}`;

        const lang = 'en-US';
        const lrsEndpoint = LRS_ENDPOINT;
        const lrsAuth = 'Basic ' + btoa(LRS_API_KEY + ':' + LRS_API_KEY_SECRETE);
        const actorName = userData.firstName + ' ' + userData.lastName;
        const actorEmail = userData.email;
        const actor = { 'name': [actorName], 'mbox': ['mailto:' + actorEmail] };
        const registrationId = userCourseData.registrationId;
        const isScorm2004 = courseData.type === 'scorm2004';

        if (courseData.type === 'scorm12' || courseData.type === 'scorm2004') {
            const manifestPath = `${coursesBaseUrl}/${courseData.path}/imsmanifest.xml`;
            fetch(manifestPath)
                .then(response => response.text())
                .then(xmlData => {
                    parseString(xmlData, { explicitArray: false, mergeAttrs: true }, (err, result) => {
                        if (!err) {
                            const courseMenu = {};
                            courseMenu.title = result.manifest.organizations.organization.title;
                            courseMenu.href = null;
                            courseMenu.content = processItems(result.manifest.organizations.organization.item);
                            if (Array.isArray(courseMenu.content)) {
                                courseMenu.content.forEach(item => setHref(item, result.manifest.resources.resource));
                            } else {
                                setHref(courseMenu.content, result.manifest.resources.resource);
                                courseMenu.content = [courseMenu.content];
                            }
                            setCourseMenuData(courseMenu);
                            if (courseMenu.content && courseMenu.content.length === 1) {
                                onPageLinkClick(courseMenu.content[0].href + (courseMenu.content[0].parameters || ''));
                                setHasChapters(false);
                            }
                            setDataLoaded(true);
                            initScorm(courseData.id, courseData.name, userData.email, isScorm2004, userCourseData.registrationId);
                        } else {
                            console.error(`Error while parsing ${manifestPath}:`, err);
                        }
                    });
                });
        }
        else if (courseData.type === 'xapi') {
            const tcConfigPath = `${coursesBaseUrl}/${courseData.path}/tc-config.js`;
            fetch(tcConfigPath)
                .then(response => response.text())
                .then(fileContent => {
                    const activityId = extractTcConfig(fileContent, 'TC_COURSE_ID');
                    const tcCourseName = JSON.parse(extractTcConfig(fileContent, 'TC_COURSE_NAME'));
                    const courseName = tcCourseName && lang in tcCourseName ? tcCourseName[lang] : 'Course';

                    const params = {};
                    params['actor'] = JSON.stringify(actor);
                    params['endpoint'] = lrsEndpoint;
                    params['auth'] = lrsAuth;
                    params['activity_id'] = activityId;
                    params['registration'] = registrationId;
                    params['grouping'] = activityId;
                    const encodedParams = encodeUrlParams(params);

                    const pageLink = `index.html?${encodedParams}`;

                    const courseMenu = {};
                    courseMenu.title = courseName;
                    courseMenu.content = [
                        {
                            title: courseName,
                            href: pageLink,
                        }
                    ];
                    setCourseMenuData(courseMenu);
                    onPageLinkClick(pageLink);
                    setHasChapters(false);
                    setDataLoaded(true);
                });
        }

    }, [courseId, coursesBaseUrl, courseData, userData, userCourseData]);

    if (!dataLoaded) {
        return <Overlay LoadingText="Loading..." />
    }

    return (
        <div className="bg-gray-100">
            <div className="flex">
                <CourseMenu courseData={courseMenuData} hasChapters={hasChapters} onPageLinkClick={onPageLinkClick} />
                <CourseViewer courseRootPath={courseData.path} hasChapters={hasChapters} coursePageLink={coursePageLink} />
            </div>
        </div>
    );
}

export default Course;
